import type { TaperStep } from "@emr/shared";
import { normalizeFrequency } from "@emr/shared";
import type { MedicineRow } from "@/lib/queries";

/**
 * Taper (escalation / de-escalation protocol) draft scaffolding shared by
 * PrescriptionTable (add flow), EditMedicineDialog (edit flow) and the
 * prescription-templates page's rows editor. Extracted 2026-11-01
 * (Batch 7 Wave 3) from three verbatim copies — behavior-identical:
 * each call site keeps its own React state and imports only the pure
 * date/chaining helpers, the row builder and the draft→TaperStep
 * conversion; the shared per-row drug search box lives in
 * TaperRowDrugSearch.tsx (component-only file). The discharge-summary
 * module has its own clones (DischargeEditMedicineDialog /
 * DischargeSummaryEditor) that are EXEMPT from this dedupe (user
 * directive 2026-10-16) and stay as-is. Pure module: no React here —
 * the UI half is in TaperRowDrugSearch.tsx so react-refresh stays clean.
 */

// One row of the escalation / de-escalation protocol builder (draft state).
// Per-step drug override fields (name/strength/composition/formulation/drugId)
// are populated only when "Allow different strengths per row" is on; null
// means the step inherits the row-level drug (legacy behaviour).
export interface DraftTaperRow {
  key: string;
  fromDate: string; // YYYY-MM-DD
  frequency: string;
  days: string;
  instructions: string;
  // Per-step drug override (only set in per-strength mode).
  name: string;        // drug name for this step (e.g. "Serlift 25mg")
  strength: string;
  composition: string | null;
  formulation: MedicineRow["formulation"] | null;
  drugId: string | null;
}

export interface DraftMedicine {
  drugId: string | null;
  name: string;
  composition: string | null;
  formulation: MedicineRow["formulation"];
  strength: string;
  frequency: string;
  taper: DraftTaperRow[] | null;
  days: string;
  instructions: string;
  // When true, each taper row carries its own drug search box so different
  // strengths of the same drug can be entered in one protocol.
  perStrength: boolean;
}

// Shared column templates for the escalation / de-escalation protocol grid —
// ONE definition used by BOTH the header labels and every row so the boxes
// and labels line up exactly. Instructions takes the flexible space; the
// date and buttons sit in fixed tracks so nothing stretches or wraps.
// TAPER_GRID: 7 tracks — From, Frequency, Days, To, Instructions, +, ×.
export const TAPER_GRID =
  "grid grid-cols-[140px_140px_64px_120px_minmax(120px,1fr)_32px_32px] items-center gap-2";
// TAPER_GRID_DRUG: 8 tracks — Drug, From, Frequency, Days, To, Instructions, +, ×.
export const TAPER_GRID_DRUG =
  "grid grid-cols-[180px_120px_120px_56px_110px_minmax(110px,1fr)_32px_32px] items-center gap-2";

export function daysFromDuration(duration: string | null | undefined): string {
  if (!duration) return "";
  const m = duration.match(/^(\d+)/);
  return m ? m[1] : "";
}

/** Date helpers on plain YYYY-MM-DD strings — pure UTC math so the result
 * never shifts with the machine timezone (IST + toISOString() shifted every
 * computed date back one day; postmortem 2026-09-20). */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

export function stepToDate(fromDate: string, days: string): string | null {
  const n = parseInt(days, 10);
  if (!fromDate || !n || n < 1) return null;
  return addDays(fromDate, n - 1);
}

/**
 * Re-chain the protocol rows AFTER index `fromIdx`: each following row's
 * From date becomes the previous row's To date + 1 day (its To date is
 * derived from From + days - 1 at render, so both ends recompute). Rows
 * before `fromIdx` are untouched. A following row whose predecessor has no
 * computable To date (blank from-date or days mid-edit) keeps its current
 * From date until the predecessor is complete again.
 */
export function rechainTaperRows(rows: DraftTaperRow[], fromIdx: number): DraftTaperRow[] {
  const next = [...rows];
  for (let i = fromIdx + 1; i < next.length; i++) {
    const prevTo = stepToDate(next[i - 1].fromDate, next[i - 1].days);
    if (prevTo) next[i] = { ...next[i], fromDate: addDays(prevTo, 1) };
  }
  return next;
}

/**
 * Build a draft taper row. In per-strength mode, the row's drug fields
 * default to the draft's row-level drug (so the user only edits the rows
 * that differ); otherwise they stay empty (the step inherits the row's
 * drug at print time).
 */
export function newTaperRow(
  fromDate: string,
  inheritDrug: DraftMedicine,
  perStrength: boolean,
): DraftTaperRow {
  return {
    key: crypto.randomUUID(),
    fromDate,
    frequency: "",
    days: "",
    instructions: "",
    name: perStrength ? inheritDrug.name : "",
    strength: perStrength ? inheritDrug.strength : "",
    composition: perStrength ? inheritDrug.composition : null,
    formulation: perStrength ? inheritDrug.formulation : null,
    drugId: perStrength ? inheritDrug.drugId : null,
  };
}

/**
 * Convert the draft's taper rows into stored TaperStep values (null when the
 * draft has no protocol). Per-step drug overrides are kept only in
 * per-strength mode; otherwise every step inherits the row-level drug.
 */
export function draftToTaperSteps(draft: DraftMedicine): TaperStep[] | null {
  return draft.taper
    ? draft.taper.map((r) => ({
        fromDate: r.fromDate,
        frequency: normalizeFrequency(r.frequency),
        days: parseInt(r.days, 10),
        instructions: r.instructions || null,
        // Per-step drug override — only set in per-strength mode; null/absent
        // means the step inherits the row-level drug (legacy behaviour).
        name: draft.perStrength ? (r.name.trim() || null) : null,
        strength: draft.perStrength ? (r.strength.trim() || null) : null,
        composition: draft.perStrength ? (r.composition ?? null) : null,
        formulation: draft.perStrength ? (r.formulation ?? null) : null,
        drugId: draft.perStrength ? (r.drugId ?? null) : null,
      }))
    : null;
}

/**
 * Convert an existing MedicineRow (with an optional taper) into a DraftMedicine
 * so the edit dialog opens with the same shape the add flow uses. Taper steps
 * (which are stored as TaperStep with `days: number`) are converted back to the
 * draft's string `days` so the grid inputs can edit them. If the stored taper
 * has per-step drug overrides, the draft starts in per-strength mode so the
 * row-level drug boxes show.
 */
export function medicineToDraft(m: MedicineRow): DraftMedicine {
  const taper: DraftTaperRow[] | null = m.taper
    ? m.taper.map((s) => ({
        key: crypto.randomUUID(),
        fromDate: s.fromDate,
        frequency: s.frequency,
        days: String(s.days),
        instructions: s.instructions ?? "",
        name: s.name ?? "",
        strength: s.strength ?? "",
        composition: s.composition ?? null,
        formulation: (s.formulation as MedicineRow["formulation"]) ?? null,
        drugId: s.drugId ?? null,
      }))
    : null;
  return {
    drugId: m.drugId,
    name: m.name,
    composition: m.composition,
    formulation: (m.formulation ?? "Tablet") as MedicineRow["formulation"],
    strength: m.strength ?? "",
    frequency: m.frequency ?? "",
    taper,
    days: daysFromDuration(m.duration),
    instructions: m.instructions ?? "",
    perStrength: taper ? taper.some((r) => r.drugId || r.name) : false,
  };
}